import { amorphicStatic } from '@haventech/amorphic';
import safeJsonStringify from 'safe-json-stringify';
import { Policy } from '../../../common/js/customer/Policy';
import { CoverpathBasePolicy } from '../../classes/coverpath_subclasses/CoverpathBasePolicy';
import { Product } from '../../../common/js/static/Product';

import {
  ProductSpecRequest,
  ProductSpecResponse,
  PremiumRequestList,
  ProductServiceUtils,
  Rider,
} from '../../../common/js/productService/ProductServiceUtils';
import { ProductQuoteResponseList, ProductQuoteResponse } from '../interfaces/CoverpathTypes';
import { UICategory, UILoggerSingleton } from '../../../common/js/logger/UILoggerSingleton';

export class ApplicationDataService {
  static async updatePolicyLocalProps(controller, policy: Policy) {
    if (policy && (policy.isCoverpathPolicy() || policy.isMMDirectPolicy())) {
      // Coverpath or MMDirect policy

      if (!policy.premiumBreakdown.baseWithoutECP) {
        // local properties are not set, so call out to Product Service Endpoint and set them

        // CoverpathBasePolicy.isEcpEligibleForPrimaryPolicy() checks if 'this' is ECP eligible so not necessarily the primary policy
        if ((policy as CoverpathBasePolicy).isEcpEligibleForPrimaryPolicy()) {
          // update non-persisted convertibleMaxYears
          await this.updateConvertibleMaxYears(controller, policy);
        }

        // update non-persisted premium related properties
        await this.updateWOPAndPremium(controller, policy, true);
      }
    }
  }

  /**
   *
   * Helper function for application.service.handleQuoteSyncing and this.updatePolicyLocalProps
   * This calls the product service to update convertibleMaxYears in the policy
   *
   * convertibleMaxYears is needed for the
   * "Extend the conversion period from 10 years to [convertibleMaxYears] years for an additional cost of [$XXX] per [month, year]?" question
   * in the Policy Information section ... see insertECPDetails function in predicate.service.ts to see how it is used
   */
  static async updateConvertibleMaxYears(controller, policy: Policy): Promise<void> {
    if (
      (!policy.isCoverpathPolicy() && !policy.isMMDirectPolicy()) ||
      !(policy as CoverpathBasePolicy).isEcpEligibleForPrimaryPolicy()
    ) {
      // not CoverpathPolicy, MMDirectPolicy, or Policy is not eligble for ECP, reset convertibleMaxYears
      (policy as CoverpathBasePolicy).convertibleMaxYears = null;
      return;
    }

    const coverpathBasePolicy = policy as CoverpathBasePolicy;

    const policyProduct = coverpathBasePolicy.product;
    let ecpProductForRequest = policyProduct;
    if (policyProduct === Product.products.CASTerm.code) {
      ecpProductForRequest = Product.products.CASTermECP.code;
    } else if (policyProduct === Product.products.MMDirectTerm.code) {
      ecpProductForRequest = Product.products.MMDirectTermECP.code;
    }

    const insuredAge = coverpathBasePolicy.insured.person.getInsuranceAge();

    const request: ProductSpecRequest = {
      insuredAge: insuredAge,
      product: ecpProductForRequest,
      productType: coverpathBasePolicy.productType,
      state: coverpathBasePolicy.insured.person.residentialAddress.state,
      duration: coverpathBasePolicy.term,
      referenceId: coverpathBasePolicy._id,
    };

    try {
      const productSpec: ProductSpecResponse = await controller.publicFetchProductSpec(request);

      const convertibleMaxYears = productSpec ? productSpec.convertibleMaxYears.valueOf() : null;
      coverpathBasePolicy.convertibleMaxYears = convertibleMaxYears;
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err.name,
        message: err.message,
        stack: err.stack,
        data: {
          request: safeJsonStringify(request),
        },
      };
      UILoggerSingleton.getInstance().error({
        module: 'ApplicationDataService',
        function: 'updateConvertibleMaxYears',
        category: UICategory.request,
        error: error,
      });
    }
  }

  /**
   *
   * Helper function for for application.service.handleQuoteSyncing and this.updatePolicyLocalProps
   * This calls the product service to update WOP and base premium in the policy
   */
  static async updateWOPAndPremium(controller, policy: Policy, onlyUpdateLocalProps?: boolean): Promise<void> {
    if (!policy.isCoverpathPolicy() && !policy.isMMDirectPolicy()) {
      // not CoverpathPolicy or MMDirectPolicy, so return
      return;
    }

    const coverpathBasePolicy = policy as CoverpathBasePolicy;

    const wopEligible = coverpathBasePolicy.isWopEligibleForPrimaryPolicy();

    const ecpEligible = coverpathBasePolicy.isEcpEligibleForPrimaryPolicy();

    // 'additionalRequests' array will be loaded with the string keywords needed to create additional requests
    const additionalRequests: Array<string> = [];

    if (ecpEligible) {
      // if ECP is supported, need to make the request for the "opposite" ECP premium value
      additionalRequests.push('oppositeECPPremium');
    }

    const requestList: PremiumRequestList = ProductServiceUtils.convertPolicyToProductServiceRequestList(
      policy,
      additionalRequests
    );

    if (wopEligible) {
      // Force fetch of waiver, for display purposes, whether or not it is selected.
      requestList.requests[0].waiver = true;
    }

    /**
     * requestList:
     *
     * first request is for selected on-screen values (Monthly/Yearly, ECP/Non ECP)
     *
     * second request (exists if policy is ECP eligible) is for the "opposite" ECP value
     * if the 'Purchase Extended Period of Convertibility' checkbox was checked when the initial quote details were filled out,
     *    than the first request will use ECP product and the third request will not
     * if it was not checked, than the first request will not use ECP product and the third request will
     */

    try {
      const productResponseList: ProductQuoteResponseList = await this.fetchMultipleProductQuotes(
        controller,
        coverpathBasePolicy.policyNumber || coverpathBasePolicy.application.customer._id,
        requestList
      );

      // first response - selected on-screen values (Monthly/Yearly, ECP/Non-ECP)
      const productResponse: ProductQuoteResponse = productResponseList.responses[0];
      const productResponseBaseValue = productResponse.base.valueOf();
      const productWaiverOfPremiumValue = (productResponse.riders || []).reduce(
        (acc, curr) => acc + curr.waiver,
        productResponse.waiver.valueOf()
      );

      if (!onlyUpdateLocalProps) {
        // update riders and set base values if 'onlyUpdateLocalProps' flag is not set to true
        this.updatePolicyRiders(coverpathBasePolicy, productResponse.riders);

        if (coverpathBasePolicy.tLICPremiumBreakdown) {
          coverpathBasePolicy.tLICPremiumBreakdown.updateBase(productResponseBaseValue);
        }

        if (coverpathBasePolicy.finalAlgoPremiumBreakdown) {
          coverpathBasePolicy.finalAlgoPremiumBreakdown.updateBase(productResponseBaseValue);
        }

        if (coverpathBasePolicy.premiumBreakdown) {
          coverpathBasePolicy.premiumBreakdown.updateBase(productResponseBaseValue);
        }

        coverpathBasePolicy.premiumBreakdown.waiver = policy.waiver ? productResponse.waiver.valueOf() : 0;
      }

      // always update baseWithECP, baseWithoutECP, and waiverOfPremiumAmount regardless of 'onlyUpdateLocalProps' flag
      if (ecpEligible) {
        // second response - "opposite" ECP value
        const oppECPQuoteResponse: ProductQuoteResponse = productResponseList.responses[1];
        const oppECPQuoteResponseBaseValue = oppECPQuoteResponse.base.valueOf();
        if (coverpathBasePolicy.hasEcp) {
          coverpathBasePolicy.premiumBreakdown.baseWithECP = productResponseBaseValue;
          coverpathBasePolicy.premiumBreakdown.baseWithoutECP = oppECPQuoteResponseBaseValue;
        } else {
          coverpathBasePolicy.premiumBreakdown.baseWithECP = oppECPQuoteResponseBaseValue;
          coverpathBasePolicy.premiumBreakdown.baseWithoutECP = productResponseBaseValue;
        }
      } else {
        // ECP not supported so set baseWithECP prop = null
        coverpathBasePolicy.premiumBreakdown.baseWithECP = null;
        coverpathBasePolicy.premiumBreakdown.baseWithoutECP = productResponseBaseValue;
      }

      coverpathBasePolicy.premiumBreakdown.baseAnnual = productResponse.annualBase;
      coverpathBasePolicy.premiumBreakdown.waiverOfPremiumAmount = wopEligible ? productWaiverOfPremiumValue : null;

      if (!onlyUpdateLocalProps) {
        controller.publicSave();
      }
    } catch (err) {
      const error = {
        isHumanRelated: false,
        data: {
          requestList: safeJsonStringify(requestList),
        },
        name: err.name,
        message: err.message,
        stack: err.stack,
      };
      UILoggerSingleton.getInstance().error({
        module: 'ApplicationDataService',
        function: 'updateWOPAndPremium',
        error: error,
        category: UICategory.request,
      });
    }
  }

  static async fetchMultipleProductQuotes(
    controller,
    requestIdentifier: string,
    requestList: PremiumRequestList
  ): Promise<ProductQuoteResponseList> {
    return Promise.all(
      requestList.requests.map((request) => controller.publicFetchProductQuote(request, requestIdentifier))
    ).then((responses) => ({
      responses,
    }));
  }

  private static updatePolicyRiders(policy: CoverpathBasePolicy, riders: Rider[]) {
    policy.updateRidersPremium(riders);
    policy.updateAlirRiderPremium();
  }
}
